import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { ArrowLeft, Plus, X, Upload, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const EditSorteo = () => {
  const { sorteoId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    prizes: [''],
    prize_images: [],
    logo: null,
    whatsapp_number: '',
    observations: '',
    is_active: true
  });

  useEffect(() => {
    fetchSorteo();
  }, [sorteoId]);

  const fetchSorteo = async () => {
    try {
      const response = await axios.get(`${API}/sorteos/${sorteoId}`);
      const sorteo = response.data;
      setFormData({
        title: sorteo.title || '',
        prizes: sorteo.prizes || [''],
        prize_images: sorteo.prize_images || [],
        logo: sorteo.logo || null,
        whatsapp_number: sorteo.whatsapp_number || '',
        observations: sorteo.observations || '',
        is_active: sorteo.is_active !== undefined ? sorteo.is_active : true
      });
    } catch (error) {
      console.error('Error fetching sorteo:', error);
      toast.error('Error al cargar el sorteo');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePrizeChange = (index, value) => {
    const newPrizes = [...formData.prizes];
    newPrizes[index] = value;
    setFormData(prev => ({ ...prev, prizes: newPrizes }));
  };

  const addPrize = () => {
    setFormData(prev => ({ ...prev, prizes: [...prev.prizes, ''] }));
  };

  const removePrize = (index) => {
    if (formData.prizes.length > 1) {
      const newPrizes = formData.prizes.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, prizes: newPrizes }));
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error('El logo no puede superar 2MB');
        return;
      }
      const reader = new FileReader();
      reader.onload = (event) => {
        setFormData(prev => ({ ...prev, logo: event.target.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePrizeImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const totalImages = formData.prize_images.length + files.length;
    
    if (totalImages > 5) {
      toast.error('Máximo 5 imágenes de premios');
      return;
    }

    files.forEach(file => {
      if (file.size > 2 * 1024 * 1024) {
        toast.error(`${file.name} supera 2MB`);
        return;
      }
      const reader = new FileReader();
      reader.onload = (event) => {
        setFormData(prev => ({
          ...prev,
          prize_images: [...prev.prize_images, event.target.result]
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const removePrizeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      prize_images: prev.prize_images.filter((_, i) => i !== index)
    }));
  };

  const removeLogo = () => {
    setFormData(prev => ({ ...prev, logo: null }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validaciones
    if (!formData.title.trim()) {
      toast.error('El título es obligatorio');
      return;
    }

    if (!formData.whatsapp_number.trim()) {
      toast.error('El número de WhatsApp es obligatorio');
      return;
    }

    const validPrizes = formData.prizes.filter(p => p.trim() !== '');
    if (validPrizes.length === 0) {
      toast.error('Agregá al menos un premio');
      return;
    }

    setSaving(true);

    try {
      const payload = {
        title: formData.title,
        prizes: validPrizes,
        prize_images: formData.prize_images,
        logo: formData.logo,
        whatsapp_number: formData.whatsapp_number,
        observations: formData.observations,
        is_active: formData.is_active
      };

      await axios.put(`${API}/sorteos/${sorteoId}`, payload);
      toast.success('Sorteo actualizado exitosamente');
      navigate('/admin/sorteos');
    } catch (error) {
      console.error('Error updating sorteo:', error);
      toast.error(error.response?.data?.detail || 'Error al actualizar el sorteo');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <Loader2 className="animate-spin mx-auto mb-4" size={40} />
        <p>Cargando sorteo...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Button variant="outline" onClick={() => navigate('/admin/sorteos')} data-testid="back-button">
        <ArrowLeft className="mr-2" size={20} />
        Volver a Sorteos
      </Button>

      <Card>
        <CardHeader>
          <CardTitle className="text-3xl">Editar Sorteo</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Título */}
            <div className="space-y-2">
              <Label htmlFor="title">Título del sorteo *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder="Ej: Sorteo Navideño 2026"
                data-testid="sorteo-title-input"
              />
            </div>

            {/* WhatsApp */}
            <div className="space-y-2">
              <Label htmlFor="whatsapp">Número de WhatsApp *</Label>
              <Input
                id="whatsapp"
                value={formData.whatsapp_number}
                onChange={(e) => handleInputChange('whatsapp_number', e.target.value)}
                placeholder="+54 9 11 1234-5678"
                data-testid="sorteo-whatsapp-input"
              />
              <p className="text-sm text-gray-500">
                Los participantes serán redirigidos a este número
              </p>
            </div>

            {/* Observaciones */}
            <div className="space-y-2">
              <Label htmlFor="observations">Observaciones</Label>
              <textarea
                id="observations"
                value={formData.observations}
                onChange={(e) => handleInputChange('observations', e.target.value)}
                placeholder="Aclaraciones para los participantes (fecha del sorteo, condiciones, etc.)"
                rows={3}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                data-testid="observations-input"
              />
            </div>

            {/* Estado activo/inactivo */}
            <div className="space-y-2">
              <Label>Estado del sorteo</Label>
              <div className="flex items-center gap-4">
                <Button
                  type="button"
                  variant={formData.is_active ? "default" : "outline"}
                  onClick={() => handleInputChange('is_active', true)}
                  className={formData.is_active ? "bg-green-600 hover:bg-green-700" : ""}
                >
                  Activo
                </Button>
                <Button
                  type="button"
                  variant={!formData.is_active ? "default" : "outline"}
                  onClick={() => handleInputChange('is_active', false)}
                  className={!formData.is_active ? "bg-red-600 hover:bg-red-700" : ""}
                >
                  Inactivo
                </Button>
              </div>
            </div>

            {/* Premios */}
            <div className="space-y-2">
              <Label>Premios *</Label>
              <div className="space-y-2">
                {formData.prizes.map((prize, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      value={prize}
                      onChange={(e) => handlePrizeChange(index, e.target.value)}
                      placeholder={`Premio ${index + 1}`}
                      data-testid={`prize-input-${index}`}
                    />
                    {formData.prizes.length > 1 && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => removePrize(index)}
                      >
                        <X size={16} />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={addPrize}
                className="mt-2"
              >
                <Plus size={16} className="mr-1" />
                Agregar premio
              </Button>
            </div>

            {/* Logo */}
            <div className="space-y-2">
              <Label>Logo del sorteo (opcional)</Label>
              {formData.logo ? (
                <div className="relative w-32">
                  <img
                    src={formData.logo}
                    alt="Logo"
                    className="w-32 h-32 object-contain border rounded"
                  />
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    className="absolute -top-2 -right-2"
                    onClick={removeLogo}
                  >
                    <X size={14} />
                  </Button>
                </div>
              ) : (
                <div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                    id="logo-upload"
                  />
                  <label htmlFor="logo-upload">
                    <Button type="button" variant="outline" asChild>
                      <span>
                        <Upload size={16} className="mr-2" />
                        Subir logo
                      </span>
                    </Button>
                  </label>
                </div>
              )}
            </div>

            {/* Imágenes de premios */}
            <div className="space-y-2">
              <Label>Imágenes de premios (opcional, máx. 5)</Label>
              <div className="flex flex-wrap gap-4">
                {formData.prize_images.map((img, index) => (
                  <div key={index} className="relative">
                    <img
                      src={img}
                      alt={`Premio ${index + 1}`}
                      className="w-24 h-24 object-cover rounded"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      className="absolute -top-2 -right-2"
                      onClick={() => removePrizeImage(index)}
                    >
                      <X size={14} />
                    </Button>
                  </div>
                ))}
                {formData.prize_images.length < 5 && (
                  <div>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handlePrizeImageUpload}
                      className="hidden"
                      id="prize-images-upload"
                    />
                    <label htmlFor="prize-images-upload">
                      <div className="w-24 h-24 border-2 border-dashed rounded flex items-center justify-center cursor-pointer hover:bg-gray-50">
                        <Plus size={24} className="text-gray-400" />
                      </div>
                    </label>
                  </div>
                )}
              </div>
            </div>

            {/* Botones */}
            <div className="flex gap-4 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/admin/sorteos')}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                {saving ? (
                  <>
                    <Loader2 className="animate-spin mr-2" size={16} />
                    Guardando...
                  </>
                ) : (
                  'Guardar Cambios'
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default EditSorteo;
